import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { QuantitySelectorUpdateEvent } from '@theme/events';

// Use Liquid's versioned asset URL so updated helpers cannot remain cached across theme revisions.
const itemsUrl = document.querySelector('mellowry-bundle-selector[data-items-url]')?.dataset.itemsUrl;
const { buildBundleItems, addBundleToCart, readShopifyCart, bundleDisplayTotal, bundleSavingsFromCart, resolveBundleVariant, resolveBundleMediaId } = await import(itemsUrl);

class MellowryBundleSelector extends HTMLElement {
  bundleQuantity = 1;
  busy = false;
  discountState = {};
  cartRevision = 0;

  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.cartRevision += 1;
    this.discountState = {};
    this.cartCheckFailed = false;
    const { signal } = this.controller;
    try {
      this.variants = JSON.parse(this.querySelector('[data-bundle-variants]').textContent);
      this.section = this.closest('.shopify-section');
      if (!this.section || !this.variants.length) return;
      const initialId = this.formComponent?.querySelector('input[name="id"]')?.value;
      const initial = this.variants.find((v) => String(v.id) === initialId && v.available) ||
        this.variants.find((v) => v.available);
      for (const slot of this.querySelectorAll('[data-color-slot]')) {
        for (const select of slot.querySelectorAll('[data-option-select]')) {
          if (initial) select.value = initial.options[Number(select.dataset.optionSelect)];
        }
      }
      this.dataset.optionDropdownsReady = 'true';
      this.bundleQuantity = initial ? Number(this.dataset.defaultQuantity || 2) : 1;
      this.section.dataset.mellowryBundleReady = 'true';
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-bundle-quantity]');
        if (!button || this.busy) return;
        const quantity = Number(button.dataset.bundleQuantity);
        this.bundleQuantity = quantity;
        this.clearError();
        this.render();
      }, { signal });
      this.addEventListener('change', (event) => {
        this.clearError();
        this.render();
        const slot = event.target.closest('[data-color-slot]');
        if (!slot) return;
        const options = [...slot.querySelectorAll('[data-option-select]')].map(select => select.value);
        this.showVariantMedia(resolveBundleVariant(this.variants, options));
      }, { signal });
      this.section.addEventListener(StandardEvents.productSelect, (event) => {
        event.promise?.then(() => requestAnimationFrame(() => {
          if (!signal.aborted) this.render();
        })).catch(() => {});
      }, { signal });
      // The embedded Liquid snapshot can be cached or lack Ajax allocation metadata. Use it only for cart context.
      this.cartHasHoodies = bundleSavingsFromCart(
        JSON.parse(this.querySelector('[data-bundle-cart]')?.textContent || '{}'), this.dataset.productId, '', ''
      ).quantity > 0;
      document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
        // Invalidate a read that started before this mutation, even while the event promise is pending.
        this.cartRevision += 1;
        event.promise?.then((result) => {
          if (signal.aborted || result.detail?.source === 'mellowry-bundle-selector') return;
          // Horizon's event carries only items, not the full discount applications. Read the canonical cart.
          this.refreshCart();
        }).catch(() => { if (!signal.aborted) this.refreshCart(); });
      }, { signal });
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.refreshCart();
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.refreshCart();
      }, { signal });
      this.render();
      this.refreshCart();
      Promise.all(['media-gallery', 'slideshow-component'].map(name => customElements.whenDefined(name)))
        .then(() => requestAnimationFrame(() => {
          if (!signal.aborted) this.showVariantMedia(this.variants.find(v => String(v.id) === this.selections()[0]));
        }));
      // Native custom elements may initialize after this module; resync their labels and sticky price.
      Promise.all(['product-form-component', 'sticky-add-to-cart'].map((name) => customElements.whenDefined(name)))
        .then(() => requestAnimationFrame(() => { if (!signal.aborted) this.render(); }));
    } catch {
      this.bundleQuantity = 1;
      // The native single-product form remains usable if enhancement cannot initialize.
      this.showError('Multiple-color selection is unavailable. You can still add a single hoodie below.');
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    if (this.section) {
      delete this.dataset.optionDropdownsReady;
      delete this.section.dataset.mellowryBundleReady;
      delete this.section.dataset.mellowryBundleActive;
    }
  }

  get formComponent() {
    return this.section?.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
  }

  selections() {
    return [...this.querySelectorAll('[data-color-slot]')].map((slot) => {
      const options = [...slot.querySelectorAll('[data-option-select]')].map((select) => select.value);
      return resolveBundleVariant(this.variants, options)?.id || '';
    });
  }

  showVariantMedia(variant) {
    const mediaId = resolveBundleMediaId(this.variants, variant, Number(this.dataset.colorOptionIndex || 0));
    if (!mediaId) return;
    const gallery = this.section?.querySelector('media-gallery');
    const slideshow = gallery?.slideshow;
    const slides = slideshow?.slides;
    if (!slides?.length) return;
    const index = slides.findIndex(slide => slide.querySelector('[data-media-id]')?.dataset.mediaId === mediaId);
    if (index >= 0) slideshow.select(index, undefined, { animate: false });
  }

  money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: this.dataset.currency, currencyDisplay: 'code',
    }).format(cents / 100);
  }

  clearError() {
    this.querySelector('[data-bundle-error]').hidden = true;
    this.querySelector('[data-bundle-cart-link]').hidden = true;
  }

  showError(message) {
    const error = this.querySelector('[data-bundle-error]');
    error.textContent = message;
    error.hidden = false;
    this.querySelector('[data-bundle-cart-link]').hidden = false;
  }

  async refreshCart() {
    const revision = ++this.cartRevision;
    const { signal } = this.controller;
    const root = window.Shopify?.routes?.root || '/';
    try {
      const cart = await readShopifyCart(window.fetch.bind(window), `${root}cart.js`);
      if (signal.aborted || revision !== this.cartRevision) return;
      this.cartCheckFailed = false;
      this.reconcileCart(cart);
    } catch {
      if (signal.aborted || revision !== this.cartRevision) return;
      // A network failure is not evidence that Shopify rejected the offer.
      this.cartCheckFailed = true;
      this.render();
    }
  }

  reconcileCart(cart) {
    if (cart.currency && cart.currency !== this.dataset.currency) return;
    this.cartCheckFailed = false;
    const state = bundleSavingsFromCart(cart, this.dataset.productId,
      this.dataset.duoTitle || 'Mellowry Cozy Duo', this.dataset.trioTitle || 'Mellowry Cozy Trio', {
        authoritative: true, duoSaving: Number(this.dataset.duoSaving) || 0, trioSaving: Number(this.dataset.trioSaving) || 0,
      });
    // Replace the whole context: removing items or the winning Trio must clear a previous Duo rejection.
    this.discountState = { duo: state.duo, trio: state.trio };
    this.cartHasHoodies = state.quantity > 0;
    if (this.section) this.render();
  }

  render() {
    const quantity = this.bundleQuantity;
    const bundle = quantity > 1;
    this.dataset.quantity = String(quantity);
    this.section.dataset.mellowryBundleActive = String(bundle);
    const selections = this.selections();
    const selected = selections.map((id) => this.variants.find((v) => String(v.id) === id));
    const single = selected[0];
    const nativeId = this.formComponent?.querySelector('input[name="id"]');
    if (nativeId) nativeId.value = single ? String(single.id) : '';
    const duoSaving = this.discountState.duo === false ? 0 : Number(this.dataset.duoSaving) || 0;
    const trioSaving = this.discountState.trio === false ? 0 : Number(this.dataset.trioSaving) || 0;
    const savingName = quantity >= 3 && trioSaving > duoSaving ? "Trio" : "Duo";
    const saving = Math.max(quantity >= 2 ? duoSaving : 0, quantity >= 3 ? trioSaving : 0);
    const regularTotal = selected.slice(0, quantity).reduce((sum, v) => sum + (v?.price || 0), 0);
    const total = bundleDisplayTotal(regularTotal, quantity, duoSaving, trioSaving);
    const pricingKnown = selected.slice(0, quantity).every(Boolean);
    let valid = Boolean(single?.available);
    {
      try { buildBundleItems(this.variants, selections, quantity); valid = true; }
      catch { valid = false; }
    }
    for (const button of this.querySelectorAll('[data-bundle-quantity]')) {
      const count = Number(button.dataset.bundleQuantity);
      const active = count === quantity;
      button.setAttribute('aria-pressed', String(active));
      button.classList.toggle('mellowry-offer--selected', active);
      button.disabled = this.busy || (count > 1 && !this.variants.some((v) => v.available));
      const offerKnown = selected.slice(0, count).every(Boolean);
      const cents = selected.slice(0, count).reduce((sum, v) => sum + (v?.price || 0), 0);
      const discounted = bundleDisplayTotal(cents, count, duoSaving, trioSaving);
      button.querySelector('[data-regular-total]').textContent = offerKnown ? this.money(discounted) : 'Unavailable';
      const original = button.querySelector('[data-original-total]');
      const offerSaving = button.querySelector('[data-offer-saving]');
      if (original) { original.hidden = !offerKnown || discounted === cents; original.textContent = this.money(cents); }
      if (offerSaving) { offerSaving.hidden = !offerKnown || discounted === cents; offerSaving.textContent = `Save ${this.money(cents - discounted)}`; }
    }
    const optionError = this.querySelector('[data-bundle-error]');
    if (!valid && !this.busy) {
      optionError.dataset.optionError = 'true';
      optionError.textContent = 'This color and length combination is unavailable. Choose another option.';
      optionError.hidden = false;
      this.querySelector('[data-bundle-cart-link]').hidden = true;
    } else if (optionError.dataset.optionError === 'true') {
      delete optionError.dataset.optionError;
      optionError.hidden = true;
    }
    this.querySelector('fieldset').hidden = false;
    for (const slot of this.querySelectorAll('[data-color-slot]')) {
      const index = Number(slot.dataset.colorSlot) - 1;
      slot.hidden = index >= quantity;
      const controls = [...slot.querySelectorAll('[data-option-select]')];
      for (const control of controls) {
        const position = Number(control.dataset.optionSelect);
        control.disabled = this.busy || index >= quantity;
        for (const option of control.options) {
          // Colors remain selectable across all lengths; length availability follows the chosen color.
          option.disabled = !this.variants.some(v => v.available && v.options[position] === option.value &&
            (position === 0 || controls.every(other => Number(other.dataset.optionSelect) === position ||
              v.options[Number(other.dataset.optionSelect)] === other.value)));
        }
      }
      const image = slot.querySelector('img');
      const variant = selected[index];
      image.hidden = !variant?.image;
      if (variant?.image) { image.src = variant.image; image.alt = variant.title; }
    }
    const nativeQuantity = this.formComponent?.querySelector('input[name="quantity"]');
    if (nativeQuantity) nativeQuantity.value = '1';
    this.dispatchEvent(new QuantitySelectorUpdateEvent(quantity));
    this.querySelector('[data-bundle-summary]').textContent = !pricingKnown ? 'Choose available options.' : bundle ?
      `${quantity} hoodies · ${this.money(total)}${saving ? ` with the eligible ${savingName} offer` : ' before checkout discounts'}.` : '';
    const note = this.querySelector('.mellowry-offers__pending');
    if (note) note.textContent = this.discountState.duo === false || this.discountState.trio === false ?
      'An advertised offer was not applied to your cart. Prices reflect the available saving; review your cart for the confirmed total.' :
      this.cartCheckFailed ? 'Cart refresh unavailable. Review your cart to confirm the saving.' :
      this.cartHasHoodies ? 'Hoodies already in your cart. Review the combined total.' :
      'Automatic bundle savings at checkout.';
    const label = `Add ${quantity} ${quantity === 1 ? "hoodie" : "hoodies"} to cart`;
    for (const button of this.section.querySelectorAll('[ref="addToCartButton"]')) {
      // The sticky control clicks the native main button, keeping one submission path.
      if (this.dataset.optionDropdownsReady === 'true') {
        button.dataset.mellowryLabel = label;
        button.setAttribute('aria-label', pricingKnown ? `${label}, ${this.money(total)}${saving ? ` with the eligible ${savingName} offer` : ""}` : 'Choose available options');
        let text = button.querySelector('.mellowry-button-label');
        if (!text) {
          text = document.createElement('span'); text.className = 'mellowry-button-label'; button.append(text);
        }
        text.textContent = pricingKnown ? `${label} · ${this.money(total)}` : 'Choose available options';
      } else {
        if (button.dataset.mellowryLabel) button.removeAttribute('aria-label');
        delete button.dataset.mellowryLabel;
        button.querySelector('.mellowry-button-label')?.remove();
      }
      button.disabled = this.busy || !valid;
      button.setAttribute('aria-busy', String(this.busy));
    }
    const sticky = this.section.querySelector('sticky-add-to-cart');
    if (sticky) {
      sticky.dataset.variantAvailable = String(valid);
      const price = sticky.querySelector('.sticky-add-to-cart__price');
      const title = sticky.querySelector('.sticky-add-to-cart__variant');
      if (price) price.textContent = pricingKnown ? this.money(total) : 'Unavailable';
      if (title) title.textContent = bundle ? selected.slice(0, quantity).map((v) => v?.title || 'Choose color').join(' + ') : single?.title || 'Choose options';
    }
  }

  async submitBundle(formComponent) {
    if (this.busy) return;
    let items;
    try {
      const properties = {};
      for (const [key, value] of new FormData(formComponent.querySelector('form'))) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string') properties[match[1]] = value;
      }
      items = buildBundleItems(this.variants, this.selections(), this.bundleQuantity, properties);
    } catch (error) { this.showError(error.message); return; }
    this.busy = true;
    const submittedAt = performance.now();
    this.clearError();
    this.render();
    const deferred = CartLinesUpdateEvent.createPromise();
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    formComponent.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'product',
      lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })), promise: deferred.promise,
    }));
    const root = window.Shopify?.routes?.root || '/';
    const cartUrl = `${root}cart.js`;
    const detail = { source: 'mellowry-bundle-selector', sourceId: formComponent.id, itemCount, productId: this.dataset.productId };
    try {
      const result = await addBundleToCart({
        fetcher: window.fetch.bind(window), addUrl: `${root}cart/add.js`, cartUrl, items,
        sections: [...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId),
        sectionsUrl: window.location.pathname + window.location.search,
      });
      this.cartRevision += 1;
      this.reconcileCart(result.cart);
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result.cart),
        detail: { ...detail, items: result.cart.items, sections: result.sections, didError: false } });
      formComponent.querySelector('[ref="liveRegion"]').textContent = `${itemCount} ${itemCount === 1 ? "hoodie" : "hoodies"} added to your cart.`;
      // Keep page-based carts usable too, without introducing any custom checkout.
      if (!document.querySelector('cart-drawer-component[auto-open]')) window.location.assign(Theme.routes.cart_url);
    } catch (error) {
      this.showError(`${error.message || 'Could not confirm the cart update.'} Review your cart before adding again.`);
      formComponent.dispatchEvent(new CartErrorEvent({ error: error.message || 'Bundle add failed', code: 'INVALID' }));
      try {
        // Shopify can partially add stock-limited items. Read the real cart instead of assuming nothing changed.
        const cart = await readShopifyCart(window.fetch.bind(window), cartUrl);
        this.cartRevision += 1;
        this.reconcileCart(cart);
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detail, items: cart.items, didError: true } });
      } catch (refreshError) { deferred.reject(refreshError); }
    } finally {
      // A very fast response must not turn one double-click into two bundle additions.
      const unlock = () => { this.busy = false; if (this.isConnected) this.render(); };
      const delay = Math.max(0, 600 - (performance.now() - submittedAt));
      if (delay) setTimeout(unlock, delay);
      else unlock();
    }
  }
}

if (!customElements.get('mellowry-bundle-selector')) customElements.define('mellowry-bundle-selector', MellowryBundleSelector);
